import type { Uuid } from '@shared/api';

/**
 * Backend now returns a fully resolved media descriptor — `url` is the ready
 * `<img>`/`<a>` source, no extra `/media/{id}/download` round-trip needed.
 *
 *  - `publicUrl: true`  → permanent CDN URL, cache aggressively (Infinity).
 *  - `publicUrl: false` → presigned S3 URL, valid ~15 minutes; cache briefly.
 *  - `url === null`     → media missing / not yet uploaded; show a placeholder.
 */
export interface MediaLink {
  id: Uuid;
  url: string | null;
  contentType: string | null;
  publicUrl: boolean;
}

/** Pull just the id out of a MediaLink (or pass-through a bare UUID). */
export function mediaLinkId(link: MediaLink | string | null | undefined): Uuid | null {
  if (!link) return null;
  return typeof link === 'string' ? link : link.id;
}

export function mediaLinkUrl(link: MediaLink | null | undefined): string | null {
  return link?.url ?? null;
}

// Scope enum mirrors the backend's media-scope contract. Note that
// certificates use the plain CERTIFICATE / ECO_CERTIFICATE names rather than
// the older PROFILE_CERTIFICATE / *_ECO_CERTIFICATE variants — the owning
// entity is inferred from where the media id is referenced.
export type MediaScope =
  | 'PROFILE_LOGO'
  | 'PROFILE_VIDEO'
  | 'CERTIFICATE'
  | 'ECO_CERTIFICATE'
  | 'PRODUCT_PHOTO'
  | 'QUOTE_ATTACHMENT'
  | 'ORDER_DOCUMENT'
  | 'CHAT_ATTACHMENT'
  | 'BUY_REQUEST_ATTACHMENT';

export type MediaStatus = 'PENDING' | 'UPLOADED' | 'DELETED';

export interface MediaRef {
  id: Uuid;
  scope: MediaScope;
  contentType: string;
  sizeBytes: number;
  status: MediaStatus;
  fileName?: string;
}

export interface PresignedUpload {
  mediaId: Uuid;
  uploadUrl: string;
  method: 'PUT';
  expiresAt: string;
}

export interface PresignedDownload {
  downloadUrl: string;
  expiresAt: string;
}

export const ALLOWED_CONTENT_TYPES: Record<string, string[]> = {
  image: ['image/jpeg', 'image/png', 'image/webp', 'image/heic'],
  video: ['video/mp4', 'video/quicktime'],
  document: [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/msword',
    'application/vnd.ms-excel',
    'text/plain',
  ],
};

export const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
